import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, EyeOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Input, Select, Form, notification, message } from "antd";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import creditIcon from "../../../../Assets/Icons/garage_money.svg";
import paymentIcon from "../../../../Assets/Icons/payments.svg";
import dataUsageIcon from "../../../../Assets/Icons/data_usage.svg";
import laptopCarIcon from "../../../../Assets/Icons/id_card-drivers.svg";
import GenericModal from "../../../../Components/Modals/GenericModal";
import {
  getVehicleCreditDetail,
  getManagerModulesLookup,
  submitVehicleCreditAdjustment,
  updateFuelCardStatus,
} from "../../../../API/FleetManager/CreditsBillings";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";
import VehicleQRCode from "../../../../Components/VehicleQRCode";

const { TextArea } = Input;

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  const messages = Object.entries(data).flatMap(([field, errors]) => {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
  });

  return messages.length ? messages.join(" | ") : fallback;
};

const CreditAdjustmentsManager = () => {
  const { canEdit } = usePageAccess("credits-billing");
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const vehicleId = location.state?.vehicleId;

  const [type, setType] = useState("add");
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [freezeModalOpen, setFreezeModalOpen] = useState(false);
  const qrRef = useRef(null);

  const [vehicleData, setVehicleData] = useState(null);
  const [moduleOptions, setModuleOptions] = useState([]);

  const fetchVehicleCredit = async () => {
    if (!vehicleId) return;

    try {
      const res = await getVehicleCreditDetail(vehicleId);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data || null;

        setVehicleData(data);
        setFrozen(data?.fuel_card?.fuel_card_status === "frozen");
      }
    } catch (err) {
      console.error("Failed to load vehicle credit detail:", err);
    }
  };

  useEffect(() => {
    fetchVehicleCredit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await getManagerModulesLookup();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setModuleOptions(
            list.map((module) => ({ label: module.name, value: module.id })),
          );
        }
      } catch (err) {
        console.error("Failed to load modules lookup:", err);
      }
    };

    fetchModules();
  }, []);

  const handleBack = () => {
    navigate("/credits-billings-manager", {
      state: { activeTab: location.state?.from || "Dashboard" },
    });
  };

  const handleCancel = () => {
    form.resetFields();
  };

  const handleConfirm = async () => {
    if (!vehicleId) {
      message.error("No vehicle selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    try {
      const res = await submitVehicleCreditAdjustment(vehicleId, {
        type,
        reason: values.reason,
        amount: Number(values.amount),
        module_id: values.category,
      });

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Adjustment applied successfully");
        form.resetFields();
        fetchVehicleCredit();
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to apply adjustment."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to apply adjustment."),
      );
    }
  };

  const handleFreezeClick = () => {
    if (frozen) {
      handleConfirmFreeze();
    } else {
      setFreezeModalOpen(true);
    }
  };

  const handleConfirmFreeze = async (enteredReason) => {
    if (!vehicleId) {
      message.error("No vehicle selected.");
      return;
    }

    const nextStatus = frozen ? "assigned" : "frozen";

    try {
      const res = await updateFuelCardStatus(vehicleId, {
        status: nextStatus,
        vehicle_id: vehicleId,
        ...(nextStatus === "frozen" ? { reason: enteredReason } : {}),
      });

      if (Math.floor(res?.status / 100) === 2) {
        setFreezeModalOpen(false);
        fetchVehicleCredit();

        notification.success({
          message: nextStatus === "frozen" ? "Card Frozen" : "Card Unfrozen",
          description:
            res?.data?.message ||
            `Fuel card has been ${nextStatus === "frozen" ? "frozen" : "unfrozen"} successfully!`,
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Action Failed",
          description: getErrorMessage(
            res?.data,
            "Failed to update fuel card status.",
          ),
          placement: "topRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "Action Failed",
        description: getErrorMessage(
          err?.response?.data,
          "Failed to update fuel card status.",
        ),
        placement: "topRight",
      });
    }
  };

  const vehicleAvatar = resolveAvatarUrl(vehicleData?.driver_avatar);

  return (
    <div className="credit-adjustments vehicle-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="header-wrapper">
        <div className="title">Vehicle Detail</div>
      </div>

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {getInitials(vehicleData?.vehicle)}
              </div>
            </div>

            <div className="profile-body">
              <div className="company-name">
                {vehicleData?.vehicle || "-"}
              </div>

              <div
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                <span
                  className="badge badge-fuel"
                  style={
                    frozen
                      ? { background: "#dc26261f", color: "#dc2626" }
                      : undefined
                  }
                >
                  Fuel Card Status: {frozen ? "Frozen" : "Active"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="tabs">
            <GenericStatCard
              title="Total Credit Balance"
              value={formatCurrency(vehicleData?.total_balance)}
              icon={creditIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Allocated"
              value={formatCurrency(vehicleData?.amount)}
              icon={dataUsageIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Available"
              value={formatCurrency(vehicleData?.available_balance)}
              icon={paymentIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Assigned Driver"
              profileImage={vehicleAvatar}
              profileLabel="Driver"
              profileName={vehicleData?.driver || "-"}
              icon={laptopCarIcon}
            />
          </div>
        </div>
      </div>

      <div className="row qr-card">
        <div className="qr-active-detail__body">
          <div className="qr-active-detail__qr-card" ref={qrRef}>
            <div className="qr-active-detail__qr-label">
              Your Active QR Code
            </div>
            <div className="qr-active-detail__qr-code">
              <VehicleQRCode
                code={vehicleData?.qr_code}
                licensePlate={vehicleData?.license_plate}
                showDownload
              />
            </div>
            <div className="qr-active-detail__qr-sublabel">
              Scan at any Precision Terminal
            </div>
          </div>
        </div>
        <div className="qr-active-detail__card-section">
          <div className="qr-active-detail__fuel-card">
            <div className="qr-active-detail__card-top">
              <div>
                <div className="qr-active-detail__card-type">
                  FLEET FUEL CARD
                </div>
                <div className="qr-active-detail__card-name">
                  {vehicleData?.vehicle || "-"}
                </div>
              </div>
              <div className="qr-active-detail__card-nfc">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                    fill="rgba(255,255,255,0.4)"
                  />
                  <path
                    d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"
                    fill="rgba(255,255,255,0.6)"
                  />
                </svg>
              </div>
            </div>
            <div className="qr-active-detail__card-number">
              {vehicleData?.fuel_card?.card_number
                ? showCardNumber
                  ? vehicleData.fuel_card.card_number
                  : vehicleData.fuel_card.card_number.replace(
                      /\d(?=\d{4})/g,
                      "*",
                    )
                : "-"}
            </div>
            <div className="qr-active-detail__card-footer">
              <div>
                <div className="qr-active-detail__card-footer-label">
                  BALANCE
                </div>
                <div className="qr-active-detail__card-footer-value">
                  {formatCurrency(vehicleData?.available_balance)}
                </div>
              </div>
              <div>
                <div className="qr-active-detail__card-footer-label">
                  EXPIRY
                </div>
                <div className="qr-active-detail__card-footer-value">
                  {vehicleData?.fuel_card?.expiry_date || "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="qr-active-detail__card-actions">
            <button
              className="qr-active-detail__card-btn"
              onClick={() => setShowCardNumber(!showCardNumber)}
            >
              <EyeOutlined />
              {showCardNumber ? "Hide Card Number" : "Show Card Number"}
            </button>
            {canEdit && (
              <button
                className={`qr-active-detail__card-btn ${frozen ? "frozen" : ""}`}
                onClick={handleFreezeClick}
              >
                <StopOutlined />
                {frozen ? "Unfreeze Card" : "Freeze Card"}
              </button>
            )}
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="adjustment-card">
          <div className="card-header">
            <div className="title">Adjustment Type</div>
            <div className="sub-title">
              Create a new account with specific role access
            </div>
          </div>

          <div className="adjustment-toggle">
            <div
              className={`toggle-btn ${type === "add" ? "active" : ""}`}
              onClick={() => setType("add")}
            >
              Add Credit
            </div>
            <div
              className={`toggle-btn ${type === "deduct" ? "active" : ""}`}
              onClick={() => setType("deduct")}
            >
              Deduct Credit
            </div>
          </div>

          <Form form={form} layout="vertical">
            <div className="form-row">
              <div className="form-group">
                <Form.Item
                  label={<>Adjustment Amount</>}
                  name="amount"
                  rules={[
                    { required: true, message: "Please enter amount" },
                    {
                      pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                      message: "Enter a valid number",
                    },
                  ]}
                >
                  <Input
                    prefix="$"
                    placeholder={`${type === "deduct" ? "-" : ""} 0.0`}
                    onKeyPress={(e) => {
                      if (!/[0-9.]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>

              <div className="form-group">
                <Form.Item
                  label={<>Category</>}
                  name="category"
                  rules={[{ required: true, message: "Please select category" }]}
                >
                  <Select placeholder="Select" options={moduleOptions} />
                </Form.Item>
              </div>
            </div>

            <div className="form-group">
              <Form.Item label="Add Reason" name="reason">
                <TextArea rows={4} placeholder="Enter a reason..." />
              </Form.Item>
            </div>
          </Form>

          <div className="card-footer">
            <Button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </Button>

            <Button
              type="primary"
              className="confirm-btn"
              onClick={handleConfirm}
            >
              Confirm Adjustment
            </Button>
          </div>

          <GenericModal
            open={freezeModalOpen}
            title="Freeze Fuel Card"
            subtitle={`Are you sure you want to freeze the fuel card for ${vehicleData?.vehicle || "this vehicle"}? This will immediately decline all active card transactions.`}
            highlightText={vehicleData?.vehicle}
            textareaLabel="Reason"
            textareaPlaceholder="Enter reason..."
            confirmText="Confirm Freeze"
            cancelText="Go Back"
            iconColor="#ef4444"
            iconBgColor="#ef44441a"
            confirmBtnColor="#EA580C"
            onCancel={() => setFreezeModalOpen(false)}
            onConfirm={handleConfirmFreeze}
          />
        </div>
      )}
    </div>
  );
};

export default CreditAdjustmentsManager;
